import React, { useMemo, useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Calculator, Loader2, TrendingUp, TrendingDown, PieChart as PieChartIcon,
  Wheat, MapPin, Wallet, Plus, Building2, Scale, Calendar, Edit2, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

import CentroCustoManager from "../components/custos/CentroCustoManager";
import CustoAlocadoForm, { categoriasCusto } from "../components/custos/CustoAlocadoForm";
import ComparativoSafras from "../components/custos/ComparativoSafras";
import ProjecaoCustos from "../components/custos/ProjecaoCustos";

const COLORS = ['#1B4332', '#2D6A4F', '#40916C', '#52B788', '#74C69D', '#95D5B2', '#B7E4C7', '#D8F3DC'];

export default function Custos() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const [periodo, setPeriodo] = useState("6");
  const [activeTab, setActiveTab] = useState("visao_geral");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCusto, setEditingCusto] = useState(null);

  const queryClient = useQueryClient();

  const { data: lancamentos = [], isLoading: loadingLancamentos } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId }, '-data', 1000)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: talhoes = [] } = useQuery({
    queryKey: ['talhoes', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Talhao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: propriedade } = useQuery({
    queryKey: ['propriedade', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Propriedade.filter({ id: propriedadeId }).then(r => r[0])
      : Promise.resolve(null),
    enabled: !!propriedadeId
  });

  const { data: centrosCusto = [] } = useQuery({
    queryKey: ['centros_custo', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.CentroCusto.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: custosAlocados = [] } = useQuery({
    queryKey: ['custos_alocados', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.CustoAlocado.filter({ propriedade_id: propriedadeId }, '-data', 500)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const deleteCustoMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.CustoAlocado.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['custos_alocados'] })
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Filter by period
  const lancamentosFiltrados = useMemo(() => {
    const mesesAtras = parseInt(periodo);
    const dataLimite = subMonths(new Date(), mesesAtras);
    return lancamentos.filter(l => new Date(l.data) >= dataLimite);
  }, [lancamentos, periodo]);

  // Despesas por categoria
  const despesasPorCategoria = useMemo(() => {
    const categorias = {};
    lancamentosFiltrados
      .filter(l => l.tipo === 'despesa')
      .forEach(l => {
        const cat = l.categoria || 'Outros';
        categorias[cat] = (categorias[cat] || 0) + (l.valor || 0);
      });
    
    // Adicionar custos alocados
    const mesesAtras = parseInt(periodo);
    const dataLimite = subMonths(new Date(), mesesAtras);
    custosAlocados
      .filter(c => new Date(c.data) >= dataLimite)
      .forEach(c => {
        const cat = c.categoria || 'Outros';
        categorias[cat] = (categorias[cat] || 0) + (c.valor || 0);
      });
    
    return Object.entries(categorias)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [lancamentosFiltrados, custosAlocados, periodo]);

  // Custos por mês
  const custosPorMes = useMemo(() => {
    const meses = [];
    for (let i = parseInt(periodo) - 1; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);
      
      const custosLancamentos = lancamentos
        .filter(l => {
          const lDate = new Date(l.data);
          return l.tipo === 'despesa' && lDate >= monthStart && lDate <= monthEnd;
        })
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      const custosAlocadosMes = custosAlocados
        .filter(c => {
          const cDate = new Date(c.data);
          return cDate >= monthStart && cDate <= monthEnd;
        })
        .reduce((acc, c) => acc + (c.valor || 0), 0);

      meses.push({
        mes: format(date, 'MMM/yy', { locale: ptBR }),
        custos: custosLancamentos + custosAlocadosMes
      });
    }
    return meses;
  }, [lancamentos, custosAlocados, periodo]);

  // Totais
  const mesesAtras = parseInt(periodo);
  const dataLimite = subMonths(new Date(), mesesAtras);
  
  const totalCustosAlocados = custosAlocados
    .filter(c => new Date(c.data) >= dataLimite)
    .reduce((acc, c) => acc + (c.valor || 0), 0);

  const totalDespesas = lancamentosFiltrados
    .filter(l => l.tipo === 'despesa')
    .reduce((acc, l) => acc + (l.valor || 0), 0) + totalCustosAlocados;

  const totalReceitas = lancamentosFiltrados
    .filter(l => l.tipo === 'receita')
    .reduce((acc, l) => acc + (l.valor || 0), 0);

  const areaTotal = propriedade?.area_total || talhoes.reduce((acc, t) => acc + (t.area_hectares || 0), 0);
  const custoPorHectareGeral = areaTotal > 0 ? totalDespesas / areaTotal : 0;

  const handleNewCusto = () => {
    setEditingCusto(null);
    setDialogOpen(true);
  };

  const handleEditCusto = (custo) => {
    setEditingCusto(custo);
    setDialogOpen(true);
  };

  const handleSuccessCusto = () => {
    queryClient.invalidateQueries({ queryKey: ['custos_alocados'] });
    setDialogOpen(false);
    setEditingCusto(null);
  };

  const getCategoriaInfo = (cat) => categoriasCusto.find(c => c.value === cat) || { label: cat, icon: "📦" };

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  if (loadingLancamentos) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Calculator className="w-8 h-8 text-purple-600" />
            Análise de Custos
          </h1>
          <p className="text-stone-600 mt-1">Alocação, projeção e comparativo de custos</p>
        </div>
        
        <div className="flex gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Últimos 3 meses</SelectItem>
              <SelectItem value="6">Últimos 6 meses</SelectItem>
              <SelectItem value="12">Últimos 12 meses</SelectItem>
              <SelectItem value="24">Últimos 24 meses</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={handleNewCusto} className="bg-purple-600 hover:bg-purple-700 gap-2">
            <Plus className="w-5 h-5" />
            Novo Custo
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">Total Despesas</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(totalDespesas)}</p>
                </div>
                <TrendingDown className="w-10 h-10 text-red-200" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Total Receitas</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(totalReceitas)}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Custo por Hectare</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(custoPorHectareGeral)}</p>
                </div>
                <MapPin className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className={`${totalReceitas - totalDespesas >= 0 ? 'bg-gradient-to-br from-[#2D6A4F] to-[#1B4332]' : 'bg-gradient-to-br from-stone-600 to-stone-700'} text-white`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Resultado</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(totalReceitas - totalDespesas)}</p>
                </div>
                <Wallet className="w-10 h-10 text-white/50" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="visao_geral" className="gap-2">
            <PieChartIcon className="w-4 h-4" />
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="alocacao" className="gap-2">
            <Building2 className="w-4 h-4" />
            Alocação
          </TabsTrigger>
          <TabsTrigger value="comparativo" className="gap-2">
            <Scale className="w-4 h-4" />
            Comparativo
          </TabsTrigger>
          <TabsTrigger value="projecao" className="gap-2">
            <Calendar className="w-4 h-4" />
            Projeção
          </TabsTrigger>
        </TabsList>

        {/* Visão Geral */}
        <TabsContent value="visao_geral" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Despesas por Categoria */}
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5 text-purple-600" />
                  Despesas por Categoria
                </CardTitle>
              </CardHeader>
              <CardContent>
                {despesasPorCategoria.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-stone-500">
                    Sem dados para exibir
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={despesasPorCategoria}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                        >
                          {despesasPorCategoria.map((entry, index) => (
                            <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                
                <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                  {despesasPorCategoria.slice(0, 6).map((cat, index) => (
                    <div key={cat.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        <span className="text-stone-600">{cat.name}</span>
                      </div>
                      <span className="font-medium">{formatCurrency(cat.value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Custos por Mês */}
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  Evolução de Custos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={custosPorMes}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                      <YAxis 
                        tickFormatter={(value) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(value)}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="custos" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Alocação de Custos */}
        <TabsContent value="alocacao" className="mt-6 space-y-6">
          {/* Centros de Custo */}
          <CentroCustoManager centrosCusto={centrosCusto} propriedadeId={propriedadeId} />

          {/* Lista de Custos Alocados */}
          <Card className="bg-white border-stone-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-purple-600" />
                Custos Alocados
                <Badge className="bg-purple-100 text-purple-700">{custosAlocados.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {custosAlocados.length === 0 ? (
                <div className="text-center py-8">
                  <Calculator className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Nenhum custo alocado</p>
                  <Button onClick={handleNewCusto} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Cadastrar Custo
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-stone-100 max-h-96 overflow-y-auto">
                  <AnimatePresence>
                    {custosAlocados.map((custo, index) => {
                      const catInfo = getCategoriaInfo(custo.categoria);
                      const centro = centrosCusto.find(c => c.id === custo.centro_custo_id);
                      const safra = safras.find(s => s.id === custo.safra_id);
                      
                      return (
                        <motion.div
                          key={custo.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ delay: index * 0.02 }}
                          className="py-3 hover:bg-stone-50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="text-2xl">{catInfo.icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-stone-800">{custo.descricao}</p>
                                <Badge variant={custo.tipo_custo === 'fixo' ? 'default' : 'secondary'} className={custo.tipo_custo === 'fixo' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>
                                  {custo.tipo_custo === 'fixo' ? '🔒 Fixo' : '📊 Variável'}
                                </Badge>
                                {custo.recorrente && (
                                  <Badge variant="outline" className="text-xs">{custo.frequencia}</Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-1 text-xs text-stone-500">
                                <span>{catInfo.label}</span>
                                {centro && <span>• {centro.nome}</span>}
                                {safra && <span>• {safra.nome}</span>}
                                <span>• {format(new Date(custo.data), "dd/MM/yyyy")}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-stone-800">{formatCurrency(custo.valor)}</p>
                              {custo.custo_unitario && (
                                <p className="text-xs text-stone-500">
                                  {formatCurrency(custo.custo_unitario)}/{custo.unidade_medida}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditCusto(custo)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteCustoMutation.mutate(custo.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comparativo */}
        <TabsContent value="comparativo" className="mt-6">
          <ComparativoSafras 
            safras={safras} 
            custosAlocados={custosAlocados}
            lancamentos={lancamentos}
          />
        </TabsContent>

        {/* Projeção */}
        <TabsContent value="projecao" className="mt-6">
          <ProjecaoCustos 
            custosAlocados={custosAlocados}
            lancamentos={lancamentos}
          />
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              {editingCusto ? "Editar Custo" : "Novo Custo Alocado"}
            </DialogTitle>
            <DialogDescription>
              {editingCusto ? "Atualize os dados do custo alocado" : "Cadastre um novo custo alocado"}
            </DialogDescription>
          </DialogHeader>
          <CustoAlocadoForm
            custo={editingCusto}
            centrosCusto={centrosCusto}
            safras={safras}
            talhoes={talhoes}
            onSuccess={handleSuccessCusto}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
